import { EmptyState } from '@kerfjs/ui/empty-state';
import { List } from '@kerfjs/ui/list';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { each, type SafeHtml } from 'kerfjs';
import { Paperclip } from 'lucide';

import { type DemandNote, notesInScope, unreadNotes } from '../../../../shared/demand-notes.js';
import { DemandLineItem } from '../../../components/demand-line-item.js';
import { DemandLineItemEditorDialog } from '../../../components/demand-line-item-editor-dialog.js';
import { DemandLineItemEvidenceDialog } from '../../../components/demand-line-item-evidence-dialog/index.js';
import type { LineOrderT } from '../../../data/api.js';
import {
  analysisReport,
  clarifyDialog,
  decisions,
  decisionsDemandId,
  editingNoteId,
  type IntakeLine,
  lineEditorDialog,
  lineEvidenceDialog,
  materializedRowMenus,
  me,
  quoteMatrix,
  selectedLineIds,
  sourceEvidence,
  sourceRowPicker,
  workspace,
} from '../../../data/state.js';
import { currentCapabilities, demandDecisionsLocked, demandNotesReadOnly } from '../../../lib/context.js';
import { pendingClarificationQuestion } from '../../../lib/line-status.js';
import { refId } from '../../../lib/memo-key.js';
import { lineRowLimit, lineRowsKey } from '../../../lib/progressive-line-rows.js';
import { ClarifyDialog } from './clarify-dialog.js';
import { DecisionDialog } from './decision-dialog.js';
import { LineNoteCount } from './line-note-count.js';
import { SourceEvidenceSection } from './source-evidence-section.js';
import { SourceRowPickerDialog } from './source-row-picker-dialog.js';
import { StatusIconCell } from './status-icon-cell.js';

export function LinesSegment({
  demandId,
  lines,
  readOnly,
}: {
  demandId: string;
  lines: IntakeLine[];
  readOnly: boolean;
}): SafeHtml {
  const selected = selectedLineIds.value;
  const editing = lineEditorDialog.value;
  const editingLine = editing?.lineId === null ? undefined : lines.find((line) => line.id === editing?.lineId);
  const evidenceLine = lines.find((line) => line.id === lineEvidenceDialog.value?.lineId);
  const clarifyLine = lines.find((line) => line.id === clarifyDialog.value?.lineId);
  const commercial = currentCapabilities().commercialDemand;
  const canReviewSources = me.value?.roles.some((role) => role === 'reviewer' || role === 'admin') ?? false;
  const canDecide = me.value?.roles.some((role) => role === 'reviewer' || role === 'admin') === true;
  // A decided demand locks its lines but not its decisions (HS2-Y50H8E).
  const decisionsLocked = demandDecisionsLocked();
  // Each line's notes: a count beside its markers and an unread status line (HS2-JRQT8A).
  const notes = (workspace.value?.notes ?? []) as DemandNote[];
  const noteReads = (workspace.value?.noteReads ?? {}) as Record<string, string>;
  const memberId = me.value?.memberId;
  // Each line's current purchase order (FR-5.18, HS2-HYDRJX), for staff and the requester alike.
  const orders = new Map(((workspace.value?.orders ?? []) as LineOrderT[]).map((order) => [order.lineItemId, order]));
  // The newest failed order per line being re-sourced (FR-5.21, HS2-SFY5DY); the list is newest first.
  const failedOrders = new Map<string, LineOrderT>();
  for (const failed of (workspace.value?.failedOrders ?? []) as LineOrderT[])
    if (!failedOrders.has(failed.lineItemId)) failedOrders.set(failed.lineItemId, failed);
  // A flagged line names the reviewer's open question instead of an inferred reason (HS2-4YCV66).
  const requesterMemberId = (workspace.value?.demand as { requesterMemberId?: string | null } | undefined)
    ?.requesterMemberId;
  const clarificationQuestion = (line: IntakeLine): string | undefined =>
    line.needsClarification ? pendingClarificationQuestion(line.id, notes, requesterMemberId)?.body : undefined;
  const lineNotes = (lineId: string): { count: number; unread: number } => {
    const list = notesInScope(notes, lineId);
    return { count: list.length, unread: unreadNotes(list, noteReads[lineId], memberId).length };
  };
  const visibleDecisions = commercial
    ? decisions.value
    : (
        (workspace.value?.decisions ?? []) as { lineItemId: string; action: string | null; isCurrent: boolean }[]
      ).filter((decision) => decision.isCurrent);
  // Row inputs, read once per render instead of once per row.
  const selectedSet = new Set(selected);
  const analysis = analysisReport.value;
  const matrix = quoteMatrix.value;
  const decisionByLine = new Map(visibleDecisions.map((decision) => [decision.lineItemId, decision]));
  const { demandId: rowMenusFor, lineIds: openedMenus } = materializedRowMenus.value;
  // A fresh mount renders the first screenful and idle chunks add the rest (HS2-HVR0HY).
  const rowLimit = lineRowLimit(lineRowsKey(demandId));
  const rows = rowLimit >= lines.length ? lines : lines.slice(0, rowLimit);
  // Lines with something to review: suggested rows, or a way to find one once the org has source
  // rows. Without any, the whole section, heading included, stays hidden (HS2-B10AFD). A read-only
  // (cancelled) demand shows its evidence without Match, Reject, or the row search (HS2-F1DNT3).
  const evidence = sourceEvidence.value;
  const evidenceEntries =
    canReviewSources && evidence?.demandId === demandId
      ? evidence.evidence.filter((entry) => entry.candidates.length > 0 || (evidence.hasSourceRows && !readOnly))
      : [];
  return (
    <div class="lines-segment" data-intake-drop>
      {lines.length === 0 ? (
        readOnly ? (
          <EmptyState title="No Line Items" />
        ) : (
          <EmptyState
            title="Drop a demand file here, or paste a list — one item per line."
            detail="You can also add an item by hand."
            icon={<LucideIcon icon={Paperclip} name="" />}
          />
        )
      ) : (
        <List
          className="demand-line-items"
          gap="xs"
          selectionMode="multiple"
          ariaLabel="Demand Line Items"
          controlInsets="tb"
        >
          {each(
            rows,
            (line) => (
              // eslint-disable-next-line kerfjs/require-data-key-in-each -- DemandLineItem puts data-key={line.id} on its ContentItem root.
              <DemandLineItem
                unreadNoteCount={lineNotes(line.id).unread}
                clarificationQuestion={clarificationQuestion(line)}
                order={orders.get(line.id)}
                failedOrder={failedOrders.get(line.id)}
                demandId={demandId}
                line={line}
                selected={selectedSet.has(line.id)}
                analysis={analysis}
                decision={decisionByLine.get(line.id)}
                matrix={matrix}
                status={
                  <>
                    <StatusIconCell line={line} question={clarificationQuestion(line)} />
                    <LineNoteCount count={lineNotes(line.id).count} />
                  </>
                }
                readOnly={readOnly}
                canDecide={canDecide}
                decisionsLocked={decisionsLocked}
              />
            ),
            {
              key: 'demand-lines',
              // Rows re-render only when their line object or something they render from changes
              // (HS2-D2G57R): everything a row reads, including the signals under it, is in this key.
              cacheKey: (line) =>
                [
                  demandId,
                  selectedSet.has(line.id),
                  refId(analysis),
                  refId(decisionByLine.get(line.id)),
                  refId(matrix),
                  readOnly,
                  canDecide,
                  decisionsLocked,
                  `${String(lineNotes(line.id).count)}/${String(lineNotes(line.id).unread)}`,
                  clarificationQuestion(line) ?? '',
                  refId(orders.get(line.id)),
                  refId(failedOrders.get(line.id)),
                  rowMenusFor === demandId && openedMenus.has(line.id),
                  decisionsDemandId.value,
                  refId(me.value),
                ].join('|'),
            },
          )}
        </List>
      )}
      <SourceEvidenceSection
        entries={evidenceEntries}
        lineDescriptions={new Map(lines.map((line) => [line.id, line.description]))}
        readOnly={readOnly}
      />
      {sourceRowPicker.value !== null ? (
        <SourceRowPickerDialog
          lineDescription={
            lines.find((item) => item.id === sourceRowPicker.value?.lineItemId)?.description ?? 'Demand line'
          }
        />
      ) : null}
      {/* A read-only demand still opens an existing line, for viewing only (HS2-SECM6V). */}
      {editing !== null && !(readOnly && editingLine === undefined) ? (
        <DemandLineItemEditorDialog
          line={editingLine}
          advancedOpen={editing.advancedOpen}
          readOnly={readOnly}
          notes={notes}
          notesReadOnly={demandNotesReadOnly()}
          memberId={memberId}
          editingNoteId={editingNoteId.value}
        />
      ) : null}
      {evidenceLine !== undefined && analysisReport.value !== null ? (
        <DemandLineItemEvidenceDialog line={evidenceLine} report={analysisReport.value} matrix={quoteMatrix.value} />
      ) : null}
      <DecisionDialog />
      {clarifyLine !== undefined ? (
        <ClarifyDialog
          lineNumber={clarifyLine.lineNumber}
          lineDescription={clarifyLine.description}
          demandStatus={(workspace.value?.demand as { status?: string } | undefined)?.status ?? ''}
          hasRequester={
            ((workspace.value?.demand as { requesterMemberId?: string | null } | undefined)?.requesterMemberId ??
              null) !== null
          }
        />
      ) : null}
    </div>
  );
}
